import { auth, firestore, database, functions } from '@breakroom-nx/apis';
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  addDoc,
  collection,
  serverTimestamp,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
} from 'firebase/firestore';

import {
  ref,
  set,
  onValue,
  query as dbQuery,
  orderByChild,
  push,
  get,
  limitToFirst,
  limitToLast,
} from 'firebase/database';

import {
  User,
  Room,
  Notification,
  userConverter,
  getUserData,
  notificationConverter,
} from '@breakroom-nx/classes';
import { FriendState, NotificationType, ChatType } from '@breakroom-nx/helpers';
import { httpsCallable } from 'firebase/functions';

export const friendAPI = createApi({
  reducerPath: 'friendApi',
  keepUnusedDataFor: 3600,
  baseQuery: fakeBaseQuery(),
  tagTypes: ['Friend', 'FriendState'],
  endpoints: (builder) => ({
    // Get User's Friends
    getFriendsForUserWithUID: builder.query<User[], string>({
      async queryFn(uid) {
        try {
          // console.info(`[USER API]: Getting friends for user ${uid}`);

          const friends: User[] = [];

          const friendsRef = collection(firestore, 'users', uid, 'friends');
          const friendsQuery = query(
            friendsRef,
            where('isRequest', '==', false)
          );

          const querySnapshot = await getDocs(friendsQuery);
          const promises = querySnapshot.docs.map(async (doc) => {
            const friendId = doc.id;
            await getUserData(friendId).then((friend) => {
              if (friend) {
                friends.push(friend);
              }
            });
          });

          await Promise.all(promises);

          // console.info(
          //   `[USER API] Friends Retrieved ${uid} : ${friends.length}`
          // );

          return { data: friends };
        } catch (err) {
          // console.log('ERROR: Unable to get friends for undefined user.');
          return { error: err };
        }
      },
      providesTags: ['Friend'],
      // createUser: build.mutation({
      //   async queryFn(data) {
      //     try {
      //       await addDoc(collection(db, 'blogs'), {
      //         ...data,
      //         timestamp: serverTimestamp(),
      //       });
      //     } catch (err) {
      //       return { error: err };
      //     }
      //   },
      // }),
    }),

    getFriendStateOfUserWithUID: builder.query<FriendState, string>({
      providesTags: ['FriendState'],
      async queryFn(uid) {
        try {
          const clientUID = auth.currentUser?.uid;

          if (!clientUID || !uid) {
            return { error: 'Client or Guest UID cannot be invalid' };
          }

          const clientFriendsPath = ref(
            database,
            'friends/' + clientUID + '/' + uid
          );
          const state = await get(clientFriendsPath).then((snapshot) => {
            if (snapshot.exists()) {
              const data = snapshot.val();
              const pending: boolean = data['isRequest'];
              if (pending) {
                return { data: FriendState.InvitePendingClientApproval };
              } else {
                return { data: FriendState.Friend };
              }
            } else {
              // // Not a Friend -> Check Client Notifications
              // const clientNotificationsRef = ref(
              //   database,
              //   'notifications/' + clientUID
              // );

              // const notificationSnap = await getDoc(notificationRef);
              // if (notificationSnap.exists()) {
              //   return { data: FriendState.RequestSent };
              // } else {
              //   return { data: FriendState.NotFriend };
              // }
              return { data: FriendState.NotFriend };
            }
          });

          return { data: state.data };
        } catch (err) {
          // console.log(`ERROR: Unable to get friend state of user: ${err}`);
          return { data: FriendState.NotFriend };
        }
      },
    }),

    updateFriendState: builder.mutation<
      FriendState,
      { friendID: string; newFriendState: FriendState; decline: boolean }
    >({
      invalidatesTags: ['FriendState'],
      async queryFn({ friendID, newFriendState, decline }) {
        try {
          const sendFriendRequest = httpsCallable(
            functions,
            'social-sendrequest'
          );
          const acceptFriendRequest = httpsCallable(
            functions,
            'social-acceptrequest'
          );
          const declineFriendRequest = httpsCallable(
            functions,
            'social-declinerequest'
          );
          const cancelFriendRequest = httpsCallable(
            functions,
            'social-cancelrequest'
          );
          const removeFriend = httpsCallable(functions, 'social-removefriend');

          switch (newFriendState) {
            case FriendState.NotFriend:
              return sendFriendRequest({ friendID: friendID })
                .then((fulfilled) => {
                  return {
                    data: fulfilled
                      ? FriendState.RequestSent
                      : FriendState.NotFriend,
                  };
                })
                .catch((err) => {
                  throw new Error(`Error updating friend state: ${err}`);
                });
            case FriendState.InvitePendingClientApproval:
              if (decline) {
                return declineFriendRequest({ friendID: friendID })
                  .then((fulfilled) => {
                    return {
                      data: fulfilled
                        ? FriendState.NotFriend
                        : FriendState.InvitePendingClientApproval,
                    };
                  })
                  .catch((err) => {
                    throw new Error(`Error updating friend state: ${err}`);
                  });
              } else {
                return acceptFriendRequest({ friendID: friendID })
                  .then((fulfilled) => {
                    return {
                      data: fulfilled
                        ? FriendState.Friend
                        : FriendState.InvitePendingClientApproval,
                    };
                  })
                  .catch((err) => {
                    throw new Error(`Error updating friend state: ${err}`);
                  });
              }
            case FriendState.RequestSent:
              return cancelFriendRequest({ friendID: friendID })
                .then((fulfilled) => {
                  return {
                    data: fulfilled
                      ? FriendState.NotFriend
                      : FriendState.RequestSent,
                  };
                })
                .catch((err) => {
                  throw new Error(`Error updating friend state: ${err}`);
                });
            case FriendState.Friend:
              return removeFriend({ friendID: friendID })
                .then((fulfilled) => {
                  return {
                    data: fulfilled
                      ? FriendState.NotFriend
                      : FriendState.Friend,
                  };
                })
                .catch((err) => {
                  throw new Error(`Error updating friend state: ${err}`);
                });
          }
        } catch (err) {
          // console.log(`ERROR: Unable to get friend state of user: ${err}`);
          return { data: FriendState.NotFriend };
        }
      },
    }),

    //! MOST LIKELY NOT NECESSARY
    // Get User's Friend Requests from Firebase
    getFriendRequestCountForUser: builder.query<number, string>({
      queryFn: async (uid) => {
        try {
          // console.info(`[USER API]: Getting friend request count for ${uid}`);

          const userFriendsPath = `users/${uid}/friends`;
          const userFriendsCollection = collection(firestore, userFriendsPath);
          const friendsQuery = query(
            userFriendsCollection,
            where('isRequest', '==', true)
          );

          const querySnapshot = await getDocs(friendsQuery);
          const friendRequestLength = querySnapshot.size;
          // console.log(`(${uid}) has ${friendRequestLength} friend requests.`);
          return { data: friendRequestLength };
        } catch (err) {
          // console.log(
          //   'ERROR: Unable to get friend requests for undefined user.'
          // );
          return { error: err };
        }
      },
    }),

    // Get User's Friend Requests from Firebase
    getFriendRequestsForUser: builder.query<Notification[], null>({
      async queryFn() {
        try {
          const uid = auth.currentUser?.uid;

          if (!uid) {
            throw new Error(`Current user's UID cannot be undefined`);
          }

          // console.info(`[USER API]: Getting friend requests for ${uid}`);

          const friendRequests: Notification[] = [];
          const userNotificationsPath = `users/${uid}/notifications`;
          const userNotificationsCollection = collection(
            firestore,
            userNotificationsPath
          ).withConverter(notificationConverter);

          const friendRequestsQuery = query(
            userNotificationsCollection,
            where('type', '==', NotificationType.Friend)
          );

          const querySnapshot = await getDocs(friendRequestsQuery);
          const promises = querySnapshot.docs.map(async (doc) => {
            const notification = doc.data();
            friendRequests.push(notification);
          });

          await Promise.all(promises);

          return { data: friendRequests };
        } catch (err) {
          // console.log(
          //   'ERROR: Unable to get friend requests for undefined user.'
          // );
          return { error: err };
        }
      },
    }),
  }),
});

export const {
  useGetFriendRequestCountForUserQuery,
  useGetFriendStateOfUserWithUIDQuery,
  useGetFriendRequestsForUserQuery,
  useUpdateFriendStateMutation,
} = friendAPI;
